import { useUser } from '@clerk/clerk-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { BaseFeedLayout } from "./layouts/BaseFeedLayout";
import { FantasyFeedLayout } from "./layouts/FantasyFeedLayout";
import { FeedItem } from "@/types/feed";
import { TarotFeedLayout } from "./layouts/TarotFeedLayout";
import { UFOFeedLayout } from "./layouts/UFOFeedLayout";
import { FlyersFeedLayout } from "./layouts/FlyersFeedLayout";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ErrorMessage } from "@/components/ui/error-message";
import { useUserSetup } from '../../hooks/useUserSetup'
import { useFeed } from '../../hooks/useFeed'
import { useEffect, useRef, useCallback } from 'react';

const FeedPost = ({ post }: { post: FeedItem }) => {
  const renderLayout = () => {
    const themeType = post.metadata?.cardType?.toLowerCase() || post.theme?.toLowerCase() || '';
    
    switch (themeType) {
      case 'fantasy':
        return <FantasyFeedLayout post={post} />;
      case 'tarot':
        return <TarotFeedLayout post={post} />;
      case 'flyers':
        return <FlyersFeedLayout post={post} />;
      case 'ufo':
        return <UFOFeedLayout post={post} />;
      default:
        return <BaseFeedLayout post={post} />;
    }
  };

  return renderLayout();
};

function Feed({ onCreateCard }: { onCreateCard: () => void }) {
  const { user } = useUser();
  const { isLoading: isCheckingSetup } = useUserSetup({ 
    redirectOnIncomplete: true 
  })
  const { data: feedItems, isLoading: isLoadingFeed, error, hasMore, loadMore } = useFeed()
  const observerTarget = useRef(null);

  console.log('🎯 Feed render:', { 
    isCheckingSetup,
    isLoadingFeed,
    itemsCount: feedItems.length,
    hasMore,
    error 
  })

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    console.log('👀 Intersection observed:', { 
      isIntersecting: target.isIntersecting,
      hasMore,
      isLoadingFeed,
      boundingRect: target.boundingClientRect,
      rootBounds: target.rootBounds
    })
    
    if (target.isIntersecting && hasMore && !isLoadingFeed) {
      console.log('🔄 Triggering loadMore')
      loadMore();
    }
  }, [loadMore, hasMore, isLoadingFeed]);

  useEffect(() => {
    console.log('🎭 Setting up intersection observer')
    const observer = new IntersectionObserver(handleObserver, {
      root: null, // viewport
      rootMargin: '100px', // Start loading earlier
      threshold: 0.1 // Trigger when even 10% is visible
    });

    const currentTarget = observerTarget.current;
    console.log('🎯 Observer target:', currentTarget)

    if (currentTarget) {
      observer.observe(currentTarget);
      console.log('🔍 Observer attached to target')
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
      observer.disconnect();
      console.log('🧹 Cleaning up observer')
    }
  }, [handleObserver]);

  // Only show loading screen during initial setup check
  if (isCheckingSetup) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    )
  }

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full h-full">
      <div className="w-full max-w-7xl mx-auto">
        <ScrollArea className="h-screen">
          <div className="max-w-xl mx-auto px-4 py-2">
            {isLoadingFeed && feedItems.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <ErrorMessage message={error} />
            ) : feedItems.length > 0 ? (
              <>
                {feedItems.map((post) => (
                  <FeedPost key={post.id} post={post} />
                ))}
                
                {/* Make sure this div is tall enough to be detected */}
                <div 
                  ref={observerTarget} 
                  className="h-20 flex items-center justify-center my-4"
                >
                  {isLoadingFeed ? (
                    <LoadingSpinner />
                  ) : (
                    hasMore ? (
                      <div className="text-gray-400">Loading more posts...</div>
                    ) : (
                      <div className="text-gray-500">No more posts to load</div>
                    )
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-lg mb-4">No cards in the feed yet</p>
                <Button onClick={onCreateCard}>Create Your First Card</Button>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

export { Feed }; 